// ── Anclar una zona rayada a los elementos que toca ──────────────────────────
//
// Un comentario sobre una zona no puede guardarse como coordenadas: la próxima
// versión de la página tiene otro layout y ese rectángulo apunta a cualquier
// cosa. El ancla real son los data-evo-id de lo que la zona cubre, que sí
// sobreviven. El rectángulo se guarda aparte y solo sirve para volver a dibujar
// la marca sobre la versión en la que se hizo.
//
// Sin React: es geometría y se prueba con rectángulos, no montando un iframe.

/** Rectángulo en coordenadas del documento de la página (px, desde el origen). */
export interface Rect {
  top: number;
  left: number;
  width: number;
  height: number;
}

/** Lo mismo, normalizado (0..1) contra el tamaño total de la página. */
export type NormalizedRect = Rect;

export function normalizeRect(rect: Rect, pageWidth: number, pageHeight: number): NormalizedRect | null {
  if (pageWidth <= 0 || pageHeight <= 0) return null;
  const acotar = (v: number): number => Math.min(1, Math.max(0, v));
  const left = acotar(rect.left / pageWidth);
  const top = acotar(rect.top / pageHeight);
  // El ancho se recorta contra el borde: una zona arrastrada fuera de la página
  // no puede quedar con left + width > 1, que es lo que el servidor rechaza.
  const width = Math.min(1 - left, acotar(rect.width / pageWidth));
  const height = Math.min(1 - top, acotar(rect.height / pageHeight));
  if (width <= 0 || height <= 0) return null;
  return { top, left, width, height };
}

/** Rectángulo a partir de dos puntos, en cualquier orden de arrastre. */
export function rectFromPoints(a: { x: number; y: number }, b: { x: number; y: number }): Rect {
  return {
    left: Math.min(a.x, b.x),
    top: Math.min(a.y, b.y),
    width: Math.abs(a.x - b.x),
    height: Math.abs(a.y - b.y),
  };
}

/** Un arrastre de dos píxeles es un clic con pulso, no una zona. */
export const MIN_REGION_SIZE = 12;

export function esZonaValida(rect: Rect): boolean {
  return rect.width >= MIN_REGION_SIZE && rect.height >= MIN_REGION_SIZE;
}

function seSuperponen(a: Rect, b: Rect): boolean {
  return !(
    a.left + a.width <= b.left ||
    b.left + b.width <= a.left ||
    a.top + a.height <= b.top ||
    b.top + b.height <= a.top
  );
}

/** Cuánto del elemento queda adentro de la zona, de 0 a 1. */
export function fraccionCubierta(elemento: Rect, zona: Rect): number {
  if (!seSuperponen(elemento, zona)) return 0;
  const area = elemento.width * elemento.height;
  if (area <= 0) return 0;
  const ancho = Math.min(elemento.left + elemento.width, zona.left + zona.width) - Math.max(elemento.left, zona.left);
  const alto = Math.min(elemento.top + elemento.height, zona.top + zona.height) - Math.max(elemento.top, zona.top);
  return (ancho * alto) / area;
}

export interface CandidatoZona {
  targetId: string;
  rect: Rect;
  /** Profundidad en el árbol. Se usa para preferir lo más específico. */
  depth: number;
}

/**
 * Qué elementos ancla una zona.
 *
 * Un solo criterio: entra lo que la zona cubre de VERDAD (`minCobertura` de su
 * propia área). Rozar el borde de una sección enorme no es haberla marcado — sin
 * el umbral, cualquier zona ancla al contenedor de todo y al modelo le llega
 * "esta zona cubre la página entera".
 *
 * No se descartan los ancestros: si alguien encierra una card, marcó la card Y su
 * título, y las dos cosas son ciertas. El umbral ya deja afuera al contenedor
 * grande, que era el único problema real. Ordenado de más profundo a menos, para
 * que lo más específico llegue primero.
 */
export function anclasDeZona(
  candidatos: CandidatoZona[],
  zona: Rect,
  opts: { minCobertura?: number; max?: number } = {},
): string[] {
  const minCobertura = opts.minCobertura ?? 0.5;
  const max = opts.max ?? 12;

  return candidatos
    .filter(c => fraccionCubierta(c.rect, zona) >= minCobertura)
    .sort((a, b) => b.depth - a.depth)
    .slice(0, max)
    .map(c => c.targetId);
}
